/** QL-375 — the mandate widget's card: header, empty and error states, the verdict sentence, the footers. Read with `useT(T)`. */
export const T = {
  en_GB: {
    applyUnavailableNoTemplates: "No mandate template is available on the goal's platform.",
    applyUnavailableNoMatchingModel:
      "Model Portfolio cannot be updated to the mandate, please change to custom portfolio or handle the deviation.",
    applyUnavailableDynamic:
      "Dynamic Portfolio cannot be updated to the mandate, please change to custom portfolio or handle the deviation.",
    applyUnavailableNotCustom: "Available on custom portfolios only — switch the type first.",
    breachBuy: "The portfolio's purchases are in breach with the mandate",
    breachGoal:
      "The forecasted allocation across goal portfolios (1Y) is in breach with the mandate",
    breachPortfolio: "The portfolio's allocation is in breach with the mandate",
    cardDescription: "How this goal's planned investments sit against the client's mandate.",
    chipExistingMandate: "Existing mandate",
    customMandateName: "Custom mandate",
    // QL-730 Q4 — where the decision card would sit, when the collection refuses the deviation.
    deviationBlockedOneTime:
      "A deviation cannot be accepted here: this collection does not allow deviations on one-time portfolios.",
    deviationBlockedRecurring:
      "A deviation cannot be accepted here: this collection does not allow deviations on recurring portfolios.",
    deviationBlockedBoth:
      "A deviation cannot be accepted here: this collection does not allow deviations on one-time or recurring portfolios.",
    // PR 701 review — the remedy follows the reason, per page: the goal page
    // offers no "Update portfolio from mandate".
    deviationBlockedRemedyPortfolio:
      "Change the portfolio, or update it from the mandate, to proceed.",
    deviationBlockedRemedyGoal: "Change the goal's portfolios to proceed.",
    deviationBlockedGeneric:
      "A deviation cannot be accepted here: this collection does not allow it.",
    fetchError: "Could not load the mandate check.",
    goalFooterPortfolioLevel:
      "This collection checks mandates per portfolio: each portfolio page carries its own verdict and decision. This combined view is informational.",
    inLineGoal: "The goal's portfolios are in line with the mandate",
    inLinePortfolio: "The portfolio is in line with the mandate",
    mandate: "Mandate",
    modeFixed: "Fixed weights",
    modeMinMax: "Min/max",
    noMandateSelected: "No mandate selected",
    noProductsNoMandateText:
      "Select a mandate template below to fill the portfolio from it, or add products first.",
    noProductsNoMandateTitle: "No products and no mandate yet",
    notEvaluableMissingAssetData:
      "A portfolio in this goal has no forecast data, so the goal's allocation cannot be measured.",
    notEvaluableNoMandateRequired:
      "This goal requires a mandate and none has resolved. Choose a template or adjust the goal.",
    notEvaluableNoNewMoney:
      "Nothing to check — this collection checks a follow-up on the new money only, and none is being invested.",
    notEvaluableNoRiskScore:
      "The goal has no risk score yet, so no mandate template can be resolved.",
    notEvaluableNoSnapshot:
      "No forecast is available for this goal yet, so the year-1 basis cannot be read.",
    nothingToCheck:
      "Nothing to check yet — none of this goal's portfolios has a composition to measure.",
    nothingToCheckPortfolio: "Nothing to check yet — this portfolio has no composition to measure.",
    portfolioFooter:
      "Whether the advice can be submitted is decided by the goal, over all its portfolios combined. This panel shows this portfolio on its own.",
    portfolioFooterPortfolioLevel:
      "This portfolio collection checks mandates per portfolio. Whether the advice can be submitted depends on every portfolio of the goal being within range or accepted.",
    replacesAtBank: (name: string) =>
      `Replaces ${name} at the bank — the check runs against the proposed mandate.`,
    retry: "Retry",
    titleBuy: "For purchases",
    titlePortfolio: "Portfolio",
    updatePortfolioFromMandate: "Update portfolio from mandate",
  },
  nb_NO: {
    applyUnavailableNoTemplates: "Ingen mandatmal er tilgjengelig på målets plattform.",
    applyUnavailableNoMatchingModel:
      "Modellportefølje kan ikke oppdateres til mandatet — bytt til egendefinert portefølje eller håndter avviket.",
    applyUnavailableDynamic:
      "Dynamisk portefølje kan ikke oppdateres til mandatet — bytt til egendefinert portefølje eller håndter avviket.",
    applyUnavailableNotCustom: "Kun tilgjengelig for egendefinerte porteføljer — bytt type først.",
    breachBuy: "Porteføljens kjøp bryter mandatet",
    breachGoal: "Den forventede allokeringen over målets porteføljer (1 år) bryter mandatet",
    breachPortfolio: "Porteføljens allokering bryter mandatet",
    cardDescription: "Hvordan dette målets planlagte investeringer ligger an mot kundens mandat.",
    chipExistingMandate: "Eksisterende mandat",
    customMandateName: "Egendefinert mandat",
    deviationBlockedOneTime:
      "Et avvik kan ikke godtas her: denne samlingen tillater ikke avvik på engangsporteføljer.",
    deviationBlockedRecurring:
      "Et avvik kan ikke godtas her: denne samlingen tillater ikke avvik på løpende porteføljer.",
    deviationBlockedBoth:
      "Et avvik kan ikke godtas her: denne samlingen tillater ikke avvik på engangs- eller løpende porteføljer.",
    deviationBlockedRemedyPortfolio:
      "Endre porteføljen, eller oppdater den fra mandatet, for å gå videre.",
    deviationBlockedRemedyGoal: "Endre målets porteføljer for å gå videre.",
    deviationBlockedGeneric: "Et avvik kan ikke godtas her: denne samlingen tillater det ikke.",
    fetchError: "Kunne ikke laste mandatkontrollen.",
    goalFooterPortfolioLevel:
      "Denne samlingen kontrollerer mandater per portefølje: hver porteføljeside har sin egen vurdering og beslutning. Denne samlede visningen er til informasjon.",
    inLineGoal: "Målets porteføljer er i tråd med mandatet",
    inLinePortfolio: "Porteføljen er i tråd med mandatet",
    mandate: "Mandat",
    modeFixed: "Faste vekter",
    modeMinMax: "Min/maks",
    noMandateSelected: "Ingen mandat valgt",
    noProductsNoMandateText:
      "Velg en mandatmal nedenfor for å fylle porteføljen fra den, eller legg til produkter først.",
    noProductsNoMandateTitle: "Ingen produkter og ingen mandat ennå",
    notEvaluableMissingAssetData:
      "En portefølje i dette målet mangler prognosedata, så målets allokering kan ikke måles.",
    notEvaluableNoMandateRequired:
      "Dette målet krever et mandat og ingen er utledet. Velg en mal eller juster målet.",
    notEvaluableNoNewMoney:
      "Ingenting å kontrollere — denne samlingen kontrollerer en oppfølging kun på de nye pengene, og ingen investeres.",
    notEvaluableNoRiskScore: "Målet har ingen risikoscore ennå, så ingen mandatmal kan utledes.",
    notEvaluableNoSnapshot:
      "Ingen prognose er tilgjengelig for dette målet ennå, så år 1-grunnlaget kan ikke leses.",
    nothingToCheck:
      "Ingenting å kontrollere ennå — ingen av målets porteføljer har en sammensetning å måle.",
    nothingToCheckPortfolio:
      "Ingenting å kontrollere ennå — denne porteføljen har ingen sammensetning å måle.",
    portfolioFooter:
      "Om rådet kan sendes inn avgjøres av målet, over alle porteføljene samlet. Dette panelet viser denne porteføljen alene.",
    portfolioFooterPortfolioLevel:
      "Denne porteføljesamlingen kontrollerer mandater per portefølje. Om rådet kan sendes inn avhenger av at hver portefølje i målet er innenfor eller akseptert.",
    replacesAtBank: (name: string) =>
      `Erstatter ${name} hos banken — kontrollen gjøres mot det foreslåtte mandatet.`,
    retry: "Prøv igjen",
    titleBuy: "For kjøp",
    titlePortfolio: "Portefølje",
    updatePortfolioFromMandate: "Oppdater porteføljen fra mandatet",
  },
} as const;
